import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DragEvent as ReactDragEvent,
  MouseEvent as ReactMouseEvent,
  SyntheticEvent,
} from 'react';
import { Copy, Download, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Double-click zoom PRESET (ported from the approved design). The wheel zooms
 * continuously in [WHEEL_SCALE_MIN, WHEEL_SCALE_MAX]; double-click always
 * toggles between normal view (1) and exactly this scale.
 */
const LB_SCALE = 2.4;

/** Wheel-zoom range: 1 = normal view, 4 = deepest inspect zoom. */
const WHEEL_SCALE_MIN = 1;
const WHEEL_SCALE_MAX = 4;

/**
 * Exponential wheel sensitivity: factor = exp(-deltaY × INTENSITY).
 * Exponential (not additive) stepping makes every wheel notch multiply the
 * scale by the same ratio, so zooming feels uniform at 1.1× and at 3×.
 */
const WHEEL_INTENSITY = 0.0015;

/**
 * deltaMode normalization: most browsers report pixel deltas (mode 0), but
 * line mode (Firefox default, mode 1) and page mode (mode 2) report tiny
 * unit counts — scale them to ~pixel equivalents before the exponential.
 */
const WHEEL_LINE_DELTA_PX = 33;
const WHEEL_PAGE_DELTA_PX = 100;

/**
 * Zoom-OUT wheel scales at or below this snap to EXACTLY 1 (with pan reset).
 * Two findings behind the 1.02 width and the direction condition applied at
 * the call site (`newScale <= prevScale`):
 *   - invisible-zoom tail: multiplicative zoom-out steps rarely land on 1.0,
 *     and a residual 1.001–1.02 "zoom" is visually indistinguishable from
 *     normal view while silently keeping zoomed-view click semantics
 *     (click = step-back instead of close);
 *   - no dead zone on the way IN: the snap applies only when arriving from
 *     above, so gentle trackpad zoom-ins from 1 (whose first notches land
 *     inside 1..1.02) are never clamped back down and small deltas still
 *     escape upward smoothly.
 */
const WHEEL_SNAP_TO_NORMAL = 1.02;

/**
 * How long after the last wheel notch the fast wheel transition stays active
 * before the transform easing decays back to the slower click-zoom curve.
 */
const WHEEL_ACTIVE_DECAY_MS = 150;

/**
 * Single/double-click discrimination window in ms. A first mouseup only
 * SCHEDULES its action; a second MOUSEDOWN landing inside this window cancels
 * it at press time (native-dblclick-style "down-cancel" semantics) and marks
 * the new press as completing a double-click — IF it stays a clean click.
 * This is what lets "single click = close / step back" coexist with "double
 * click = zoom" without the close firing on the first half of a double-click,
 * and — because the cancel happens on the DOWN, not the second UP — the
 * pending action can never fire mid-gesture (e.g. yanking the view back to
 * normal in the middle of a click-then-drag).
 */
const CLICK_DELAY_MS = 230;

/**
 * Total pointer displacement (Euclidean, px) above which a press is a drag,
 * not a click. Once crossed, the eventual mouseup is swallowed — panning must
 * never trigger the single-click action.
 */
const DRAG_THRESHOLD_PX = 3;

/**
 * Context-menu clamp margins: the menu's estimated footprint (~188px wide +
 * padding, two rows tall). Clamping `clientX/clientY` against
 * `window.inner* - margin` keeps the menu fully on-screen when invoked near
 * the right/bottom window edges.
 */
const MENU_CLAMP_X_PX = 196;
const MENU_CLAMP_Y_PX = 104;

/** How long the Save-as… object URL stays alive after the download click. */
const OBJECT_URL_REVOKE_MS = 4000;

/**
 * Mutable per-press bookkeeping. Lives in a ref (not state) because it is
 * consulted/updated inside mousemove at pointer frequency — re-rendering per
 * move for data no one renders would be wasteful, and the click-vs-drag
 * decision must read the CURRENT values synchronously during event dispatch.
 */
interface DragState {
  /** Pointer position at mousedown (viewport coords). */
  startX: number;
  startY: number;
  /** Pan offset at mousedown — drag applies displacement on top of this. */
  panX: number;
  panY: number;
  /** Crossed the drag threshold: the eventual mouseup is NOT a click. */
  moved: boolean;
  /**
   * This press only dismissed the open context menu; both the move and the
   * mouseup of this press must be ignored entirely (design's `swallow` flag),
   * so dismissing the menu can never double as "close the viewer".
   */
  swallow: boolean;
  /**
   * This press's mousedown landed inside the discrimination window and
   * cancelled the pending single-click action; if the press resolves as a
   * clean click (no drag), its mouseup completes a double-click. If it turns
   * into a drag, the whole click-pair simply merges into the drag.
   */
  fromPendingClick: boolean;
}

interface ImageLightboxProps {
  /** Fully-qualified image URL (including any auth token query param). */
  src: string;
  /** Alt text for both the inline thumbnail and the enlarged view. Carries the filename. */
  alt: string;
  /** Optional extra classes applied to the inline thumbnail `<img>`. */
  thumbnailClassName?: string;
  /**
   * Optional error handler wired to BOTH the thumbnail `<img>` and the
   * fullscreen `<img>`. Pass the handler from `useImageWithMediaToken`
   * to enable the one-shot media-token retry on 401.
   */
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
}

interface MenuItemButtonProps {
  icon: React.JSX.Element;
  label: string;
  onClick: () => void;
}

/** One row of the custom context menu: icon + label, full-width hit area. */
function MenuItemButton({ icon, label, onClick }: MenuItemButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        flex w-full cursor-pointer items-center gap-2.5 rounded-[10px] border-0
        bg-transparent px-2.5 py-2 text-left text-[13px] font-bold
        text-foreground hover:bg-[rgba(96,110,132,0.14)]
      "
    >
      <span className="inline-flex size-[15px] shrink-0 opacity-75">{icon}</span>
      {label}
    </button>
  );
}

/**
 * Wraps an inline image thumbnail with a fullscreen lightbox Dialog.
 *
 * Usage:
 *   <ImageLightbox src={tokenisedUrl} alt="attachment" thumbnailClassName="..." />
 *
 * Interaction matrix (ported from the approved claude.ai/design reference):
 *   - Normal view: single click anywhere closes the viewer (after the
 *     double-click discrimination window); double click zooms in AT the
 *     clicked point; Esc closes; right click opens a custom Copy / Save as…
 *     menu.
 *   - Zoomed view (any scale > 1): single click steps back to normal view
 *     (does NOT close); double click also un-zooms; click-drag pans the image
 *     (and suppresses the click action); Esc closes outright; right click
 *     opens the menu.
 *   - Mouse wheel (both views): continuous cursor-anchored zoom in [1, 4] —
 *     the pixel under the cursor stays put while the image grows/shrinks
 *     around it. Scrolling back down to ~1 snaps to exactly 1 and restores
 *     normal-view semantics. Layered on top of the click machine: a wheel
 *     mid-drag is ignored, a wheel over the open context menu dismisses it.
 *
 * All mouse handling lives on the fullscreen DialogContent as plain React
 * mouse events (no pointer capture — jsdom-friendly, and the fullscreen
 * overlay already receives every event). A per-press `DragState` ref plus a
 * 230 ms timer implement the click/double-click/drag discrimination; see the
 * handlers below for the event-ordering rationale.
 *
 * ESC LAYERING (bug fix): the chat panel installs a document-level
 * bubble-phase Escape listener that deselects the whole conversation
 * (`ChatPanel`'s `shouldIgnoreConversationEscape`). Relying on that guard to
 * *detect* this open dialog by querying `[data-state="open"]` is racy — the
 * dialog's `data-state` is React state that has not necessarily flipped (nor
 * been read consistently) during the synchronous keydown dispatch. Instead
 * this component OWNS its dismissal via a **capture-phase** document keydown
 * listener installed while open: capture-phase document listeners are
 * guaranteed by the DOM event model to run before any bubble-phase document
 * listener, so we intercept Escape first, close only the lightbox, and call
 * `stopImmediatePropagation()` so the chat panel's listener never sees this
 * keystroke. One Escape closes the image; the NEXT Escape (lightbox already
 * gone, no capture listener) reaches the chat panel and closes the
 * conversation. Deterministic — no dependency on listener registration order
 * or React flush timing. Escape closes UNCONDITIONALLY — even while zoomed —
 * it never merely un-zooms.
 */
export function ImageLightbox({
  src,
  alt,
  thumbnailClassName,
  onError,
}: ImageLightboxProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [wheelActive, setWheelActive] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const drag = useRef<DragState | null>(null);
  const clickTimer = useRef<number | null>(null);
  /** Re-arming decay timer behind the fast wheel-zoom transition. */
  const wheelDecayTimer = useRef<number | null>(null);
  /** The fullscreen `<img>` — measured for pan clamping and drawn for Copy. */
  const baseRef = useRef<HTMLImageElement | null>(null);
  /**
   * The DialogContent DOM node — target of the native wheel listener. Held
   * in STATE (fed by a callback ref), not a plain ref: Radix's Portal
   * commits its children in a SECOND, layout-effect-driven commit (its
   * `mounted` flip), and React flushes the first commit's passive effects
   * before that second commit assigns refs — so an effect keyed on [open]
   * reading a plain object ref would still see null and never re-run. A
   * state value re-fires the effect exactly when the node exists.
   */
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  /**
   * Mirror refs for scale/pan. INVARIANT: every read of the view from EVENT
   * HANDLERS (mouse AND wheel) goes through these refs; the render-state
   * copies exist only for rendering. Two reasons: (1) the wheel handler is
   * a native listener registered in an effect — it would close over stale
   * state, and several wheel events can arrive between two renders, each
   * needing the value the previous one just wrote; (2) React 18 batches a
   * native wheel's setState, so a mouse handler firing before that flush
   * would read render state one frame stale and corrupt its drag baseline /
   * click semantics if it did not go through the refs.
   */
  const scaleRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });

  /** "Zoomed" is now DERIVED: any scale above 1 (wheel or double-click). */
  const zoomed = scale > 1;

  /**
   * ALL scale/pan writes go through these two so the mirror refs never lag
   * the latest write. WHY refs at all: the wheel handler is a NATIVE listener
   * registered in an effect keyed on [open] — it would otherwise close over
   * the scale/pan of the render in which it was attached. And a "sync the ref
   * in an effect" mirror is not enough either: several wheel events can
   * arrive between two React renders, and each one's anchoring math must
   * chain off the value the PREVIOUS wheel event just wrote, not the last
   * rendered one.
   */
  const setViewScale = useCallback((next: number): void => {
    scaleRef.current = next;
    setScale(next);
  }, []);
  const setViewPan = useCallback((next: { x: number; y: number }): void => {
    panRef.current = next;
    setPan(next);
  }, []);

  /**
   * Reset EVERY piece of interaction state. Called on every open/close
   * transition so a reopened viewer always starts in normal view, and a
   * pending discrimination timer can never fire against a closed (or
   * re-opened) dialog.
   */
  const resetView = useCallback((): void => {
    if (clickTimer.current !== null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    if (wheelDecayTimer.current !== null) {
      window.clearTimeout(wheelDecayTimer.current);
      wheelDecayTimer.current = null;
    }
    drag.current = null;
    setViewScale(1);
    setViewPan({ x: 0, y: 0 });
    setDragging(false);
    setWheelActive(false);
    setMenu(null);
  }, [setViewScale, setViewPan]);

  const close = useCallback((): void => {
    resetView();
    setOpen(false);
  }, [resetView]);

  useEffect(() => {
    if (!open) return;
    function onEscapeCapture(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      // Intercept before the chat panel's bubble-phase document listener so a
      // single Escape closes ONLY the lightbox, never the conversation.
      e.stopImmediatePropagation();
      e.preventDefault();
      close();
    }
    document.addEventListener('keydown', onEscapeCapture, { capture: true });
    return () => {
      document.removeEventListener('keydown', onEscapeCapture, { capture: true });
      // Timers must never outlive the open viewer: a pending single-click
      // timeout firing after close/unmount would call setState against a
      // dead dialog (or instantly close a reopened one), and a pending wheel
      // decay would do the same for the transition state.
      if (clickTimer.current !== null) {
        window.clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      if (wheelDecayTimer.current !== null) {
        window.clearTimeout(wheelDecayTimer.current);
        wheelDecayTimer.current = null;
      }
    };
  }, [open, close]);

  /**
   * Clamp a pan offset so an image at `atScale` cannot be dragged fully
   * off-screen. Based on the RENDERED image box (`offsetWidth/Height`); the
   * `Math.max(40, …)` floor keeps a small ±40px range even when the box
   * measures 0 (jsdom, or an image that failed to load), so the machine
   * stays well-defined instead of dividing into NaN territory. Stable
   * identity (reads only refs/window) so the native wheel handler and its
   * registration effect can depend on it without re-subscribing per render.
   */
  const clampPan = useCallback(
    (x: number, y: number, atScale: number): { x: number; y: number } => {
      const el = baseRef.current;
      const w = el ? el.offsetWidth : 600;
      const h = el ? el.offsetHeight : 450;
      const maxX = Math.max(40, (w * atScale - window.innerWidth) / 2 + 60);
      const maxY = Math.max(40, (h * atScale - window.innerHeight) / 2 + 60);
      return { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
    },
    [],
  );

  const unzoom = (): void => {
    setViewScale(1);
    setViewPan({ x: 0, y: 0 });
  };

  /**
   * Double-click zoom to the LB_SCALE preset, centred on the clicked point:
   * after scaling by LB_SCALE around the image centre, translating by
   * `-(click - centre) * (LB_SCALE - 1)` puts the clicked pixel back under
   * the cursor (clamped to the pannable range). The centre is STATIC: the
   * stage is flex-centered in the fullscreen overlay, so the untransformed
   * centre C0 is exactly the viewport centre, and zoomAt only ever runs from
   * normal view (scale 1, pan 0) — C = C0. Deliberately NOT measured via
   * getBoundingClientRect: a double-click racing a still-running snap-back
   * transition would read the INTERPOLATED transform and mix animated
   * geometry into target-frame math.
   */
  const zoomAt = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const px = e.clientX - window.innerWidth / 2;
    const py = e.clientY - window.innerHeight / 2;
    setViewPan(clampPan(-px * (LB_SCALE - 1), -py * (LB_SCALE - 1), LB_SCALE));
    setViewScale(LB_SCALE);
  };

  /**
   * Continuous cursor-anchored wheel zoom. Registered NATIVELY (see the
   * effect below) so it may run with `passive: false`; therefore it must
   * read the CURRENT view through scaleRef/panRef, never through closed-over
   * state (see setViewScale/setViewPan for why an effect-synced mirror would
   * not be enough either).
   */
  const handleWheel = useCallback(
    (e: WheelEvent): void => {
      // Cancel first, always: without this the wheel scrolls the chat
      // behind the dialog while the user thinks they are zooming.
      e.preventDefault();
      // Same mid-gesture family as F4's down-cancel: a wheel inside the
      // discrimination window means the user is now zooming, not clicking —
      // the pending single-click action (close / step-back) must not fire
      // mid-zoom.
      if (clickTimer.current !== null) {
        window.clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      // A wheel over the open context menu dismisses it and zooms on
      // (no-op state update when the menu is already closed).
      setMenu(null);

      const prevScale = scaleRef.current;
      const prevPan = panRef.current;

      // Normalize non-pixel deltaModes to ~pixels before the exponential.
      let deltaY = e.deltaY;
      if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) deltaY *= WHEEL_LINE_DELTA_PX;
      else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) deltaY *= WHEEL_PAGE_DELTA_PX;
      const factor = Math.exp(-deltaY * WHEEL_INTENSITY);
      const newScale = Math.min(
        WHEEL_SCALE_MAX,
        Math.max(WHEEL_SCALE_MIN, prevScale * factor),
      );

      // Direction-aware snap-to-normal — see WHEEL_SNAP_TO_NORMAL.
      const snapped = newScale <= WHEEL_SNAP_TO_NORMAL && newScale <= prevScale;
      let nextScale: number;
      let nextPan: { x: number; y: number };
      if (snapped) {
        // Back to (effectively) 1: snap exactly and recentre — restores
        // normal-view semantics (single click = close again).
        nextScale = 1;
        nextPan = { x: 0, y: 0 };
      } else {
        nextScale = newScale;
        // Cursor anchoring — keep the pixel under the cursor stationary.
        // The stage is flex-centered in the fullscreen overlay, so its
        // UNTRANSFORMED centre C0 is exactly the viewport centre; the stage
        // scales about that centre and then translates by pan, so the
        // transformed centre is C = C0 + pan (scaling about the centre does
        // not move it). A screen point P therefore shows the unscaled-image
        // offset x = (P − C) / scale. Requiring the same offset to sit at P
        // after the zoom,
        //   P = C0 + pan  + scale    · x
        //   P = C0 + pan′ + newScale · x
        // gives pan′ = pan + (scale − newScale) · x  (same for y), clamped.
        // C is computed STATICALLY (viewport centre + target-frame pan),
        // NEVER via getBoundingClientRect: during the 0.12s/0.28s transform
        // transition the rect reports the INTERPOLATED transform, and mixing
        // that animated frame with target-frame scale/pan refs makes
        // multi-notch wheels drift off the cursor.
        const cx = window.innerWidth / 2 + prevPan.x;
        const cy = window.innerHeight / 2 + prevPan.y;
        const x = (e.clientX - cx) / prevScale;
        const y = (e.clientY - cy) / prevScale;
        nextPan = clampPan(
          prevPan.x + (prevScale - newScale) * x,
          prevPan.y + (prevScale - newScale) * y,
          newScale,
        );
      }
      setViewScale(nextScale);
      setViewPan(nextPan);

      // Grab-and-zoom: a wheel DURING a held (non-swallow) press RE-BASES
      // the press so zooming and dragging compose without jumps — the
      // press's pan baseline and start point move to the values just
      // applied, and the press is latched as a pan gesture (moved = true)
      // so its release can never resolve as a click. This replaces the old
      // "ignore wheel mid-drag" guard: ignoring left the stale baseline
      // around anyway (the first post-latch mousemove snapped the image by
      // the discarded wheel delta), and a scale-1-origin press never set
      // `dragging`, so post-wheel panning chased the easing curve. Swallow
      // (menu-dismiss) presses stay untouched: the zoom still applies and
      // their release is already inert.
      const d = drag.current;
      if (d && !d.swallow) {
        d.panX = nextPan.x;
        d.panY = nextPan.y;
        d.startX = e.clientX;
        d.startY = e.clientY;
        d.moved = true;
        d.fromPendingClick = false;
        // 1:1 pointer tracking (transition 'none') while the button is held
        // at a zoomed scale; a snap back to 1 ends the pan gesture.
        setDragging(nextScale > 1);
      }

      // Fast, snappy easing while the wheel is in motion; re-arm the decay
      // on every notch so it flips back 150 ms after the LAST one.
      setWheelActive(true);
      if (wheelDecayTimer.current !== null) window.clearTimeout(wheelDecayTimer.current);
      wheelDecayTimer.current = window.setTimeout(() => {
        wheelDecayTimer.current = null;
        setWheelActive(false);
      }, WHEEL_ACTIVE_DECAY_MS);
    },
    [clampPan, setViewScale, setViewPan],
  );

  useEffect(() => {
    // contentNode is null whenever the dialog is closed — no [open] gate
    // needed; the callback ref clears it on unmount.
    if (!contentNode) return;
    // NATIVE, non-passive listener — deliberately NOT React's onWheel:
    // browsers register root/document-level wheel listeners as passive by
    // default (and React attaches its delegated listeners at the root), so
    // preventDefault() from a React onWheel can be silently ignored and the
    // chat behind the dialog scrolls while the user zooms. Only a listener
    // attached directly to the node with { passive: false } is guaranteed
    // cancelable.
    contentNode.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      contentNode.removeEventListener('wheel', handleWheel);
    };
  }, [contentNode, handleWheel]);

  const onOverlayMouseDown = (e: ReactMouseEvent<HTMLDivElement>): void => {
    if (e.button !== 0) return;
    if (menu !== null) {
      // While the menu is open, the next press anywhere else only dismisses
      // it. Mark the press `swallow` so its move/up phases are inert — the
      // dismissal must not double as a viewer click (which would close it).
      setMenu(null);
      drag.current = {
        startX: 0,
        startY: 0,
        panX: 0,
        panY: 0,
        moved: false,
        swallow: true,
        fromPendingClick: false,
      };
      return;
    }
    // Down-cancel double-click semantics (like the browser's native
    // dblclick): a mousedown landing inside the discrimination window IS the
    // second half of a double-click, so cancel the pending single-click
    // action HERE, at press time — it must never fire mid-gesture — and
    // remember on the DragState that a clean release completes the pair.
    const fromPendingClick = clickTimer.current !== null;
    if (clickTimer.current !== null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    // View reads below go through the mirror refs, per their invariant: a
    // native wheel's batched setState may not have flushed yet, and a stale
    // render-state pan/scale here would corrupt this press's baseline.
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      panX: panRef.current.x,
      panY: panRef.current.y,
      moved: false,
      swallow: false,
      fromPendingClick,
    };
    // Only a zoomed image can pan, so only then does the press cursor flip
    // to "grabbing" (and the transform transition switch off).
    if (scaleRef.current > 1) setDragging(true);
  };

  const onOverlayMouseMove = (e: ReactMouseEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (!d || d.swallow) return;
    // Robustness on top of the design source: if the mouseup happened outside
    // the window (button no longer held per `e.buttons`), the up event was
    // lost — abort the drag instead of panning with the button released.
    if (e.buttons === 0) {
      drag.current = null;
      setDragging(false);
      return;
    }
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // One-way latch: any move beyond the threshold makes this press a drag.
    if (!d.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) d.moved = true;
    // Ref reads per the mirror-ref invariant (render state may lag a wheel).
    if (d.moved && scaleRef.current > 1) {
      setViewPan(clampPan(d.panX + dx, d.panY + dy, scaleRef.current));
    }
  };

  const onOverlayMouseUp = (e: ReactMouseEvent<HTMLDivElement>): void => {
    // Only a left-button release may resolve a press: a middle/right release
    // must not consume the DragState of a still-held left press (and then
    // schedule a click action against it).
    if (e.button !== 0) return;
    const d = drag.current;
    drag.current = null;
    setDragging(false);
    // No press bookkeeping, a menu-dismiss press, or a drag: not a click —
    // no single/double discrimination. A drag also swallows
    // `fromPendingClick`: click-then-drag merges into the drag.
    if (!d || d.swallow || d.moved) return;
    if (d.fromPendingClick) {
      // Clean release of a press whose down cancelled the pending
      // single-click action: the pair is a DOUBLE click — toggle zoom. (The
      // timer cannot still be pending here; the down already cleared it.)
      // Zoom read through the ref per the mirror-ref invariant.
      if (scaleRef.current > 1) {
        unzoom();
      } else {
        zoomAt(e);
      }
    } else {
      // First mouseup: don't act yet — a second press may still turn this
      // into a double-click. Schedule the single-click action instead:
      // zoomed → step back to normal view; normal → close the viewer. The
      // ref is read at FIRE time, so the decision reflects any zoom that
      // happened during the discrimination window.
      clickTimer.current = window.setTimeout(() => {
        clickTimer.current = null;
        if (scaleRef.current > 1) {
          unzoom();
        } else {
          close();
        }
      }, CLICK_DELAY_MS);
    }
  };

  const onOverlayContextMenu = (e: ReactMouseEvent<HTMLDivElement>): void => {
    // Replace the native menu with the custom Copy / Save as… menu.
    e.preventDefault();
    e.stopPropagation();
    // A right click aborts any pending single-click action AND any
    // in-progress drag — opening the menu must not race a scheduled close.
    if (clickTimer.current !== null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    drag.current = null;
    setDragging(false);
    setMenu({
      x: Math.min(e.clientX, window.innerWidth - MENU_CLAMP_X_PX),
      y: Math.min(e.clientY, window.innerHeight - MENU_CLAMP_Y_PX),
    });
  };

  /**
   * Copy the ALREADY-LOADED fullscreen `<img>` to the clipboard. The
   * clipboard API only accepts PNG for images, so the (possibly JPEG/WebP)
   * source is re-encoded by drawing onto a canvas at natural size. The media
   * URL is same-origin (token in query param), so the canvas is not tainted.
   */
  const doCopy = async (): Promise<void> => {
    setMenu(null);
    try {
      if (
        !navigator.clipboard ||
        !window.isSecureContext ||
        typeof ClipboardItem === 'undefined'
      ) {
        throw new Error('clipboard unavailable');
      }
      const img = baseRef.current;
      // drawImage() with a not-fully-decoded image is a spec-mandated SILENT
      // no-op ("if the image is not fully decodable, return without drawing")
      // — the canvas would stay blank and a perfectly valid blank PNG would
      // land on the clipboard under a SUCCESS toast. Bail out with a distinct
      // message instead of throwing into the generic clipboard-failure catch.
      if (!img || !img.complete || img.naturalWidth === 0) {
        toast('Copy failed', { description: 'Image has not finished loading yet' });
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.offsetWidth || 1;
      canvas.height = img.naturalHeight || img.offsetHeight || 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas 2d unavailable');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });
      if (!blob) throw new Error('png encode failed');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast('Image copied', { description: alt });
    } catch {
      toast('Copy failed', { description: 'Clipboard unavailable — use Save as… instead' });
    }
  };

  /**
   * Download the ORIGINAL bytes (re-fetch of the same-origin tokenised URL —
   * unlike Copy there is no PNG re-encode, format is preserved) under the
   * filename carried by `alt`.
   */
  const doSave = async (): Promise<void> => {
    setMenu(null);
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`fetch failed: ${String(res.status)}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = alt;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke AFTER the browser has had time to start the download — an
      // immediate revoke races the click on slower machines.
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, OBJECT_URL_REVOKE_MS);
      toast('Saving image', { description: alt });
    } catch {
      toast('Save failed', { description: 'Could not download this image' });
    }
  };

  /**
   * The top bar and the context menu are chrome, not viewer surface: their
   * mouse events must never reach the overlay's click state machine (a click
   * on a pill must not close the viewer, a press on the menu must not count
   * as the "dismiss" press).
   */
  const stopMouse = (e: ReactMouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  };

  const handleOpenChange = (next: boolean): void => {
    // Reset on BOTH transitions so a reopened viewer always starts in normal
    // view regardless of how the previous session ended.
    resetView();
    setOpen(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <img
          src={src}
          alt={alt}
          className={[
            'cursor-zoom-in',
            thumbnailClassName,
          ]
            .filter(Boolean)
            .join(' ')}
          onError={onError}
        />
      </DialogTrigger>

      {/*
        Override DialogContent's default shadcn styles so the overlay is
        truly fullscreen (inset-0, no rounded corners, no padding, no
        border). `sm:rounded-none` is needed IN ADDITION to `rounded-none`:
        the vendored base carries `sm:rounded-[18px]`, which sits in a
        different tailwind-merge variant bucket than the bare `rounded-none`
        and would otherwise survive — and, combined with overflow-hidden,
        visibly clip the overlay's corners at ≥640px viewports.
        The default close button (X, top-right) is hidden via
        [&>button]:hidden — the top bar provides its own round close button.
        The backdrop tint + blur, user-select and the state-dependent cursor
        (zoom-in → grab → grabbing) are inline: they carry the design's
        literal values / change per interaction state.
      */}
      <DialogContent
        ref={setContentNode}
        data-zoomed={zoomed ? 'true' : 'false'}
        className="
          fixed inset-0 flex h-screen w-screen max-w-none translate-x-0 translate-y-0
          items-center justify-center overflow-hidden rounded-none border-none p-0
          shadow-none sm:rounded-none [&>button]:hidden
          data-[state=open]:animate-in data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
          data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
        "
        // Suppress the default slide-in/out animation classes added by shadcn
        // for positioned modals — they use translate which conflicts with our
        // fullscreen layout.  We keep fade + zoom only (declared above).
        style={{
          // The design's overlay tint is a single rgba(30,38,54,0.62) — but
          // the vendored DialogContent always renders DialogOverlay
          // (bg-black/40) underneath this content layer, so the two
          // composite: total = x + 0.4(1 − x). Solving x + 0.4(1 − x) = 0.62
          // gives x ≈ 0.3667 → 0.37 here (0.37 + 0.4 × 0.63 ≈ 0.62), so the
          // EFFECTIVE backdrop matches the approved design instead of
          // double-dimming to ~0.77.
          background: 'rgba(30,38,54,0.37)',
          backdropFilter: 'blur(7px)',
          WebkitBackdropFilter: 'blur(7px)',
          userSelect: 'none',
          cursor: zoomed ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
        }}
        onMouseDown={onOverlayMouseDown}
        onMouseMove={onOverlayMouseMove}
        onMouseUp={onOverlayMouseUp}
        onContextMenu={onOverlayContextMenu}
        // Kill the native image ghost-drag — it would swallow the mousemove
        // stream our pan implementation depends on.
        onDragStart={(e: ReactDragEvent<HTMLDivElement>) => {
          e.preventDefault();
        }}
      >
        {/* Visually-hidden title required by Radix for a11y */}
        <DialogTitle className="sr-only">{alt}</DialogTitle>

        {/*
          Zoom/pan stage: the transform lives on this wrapper (not the <img>)
          so the neu card frame scales and pans together with the image.
          Transition three-way: while dragging it is OFF (the transform must
          track the pointer 1:1, not chase it through an easing curve); while
          the wheel is in motion a short ease-out keeps successive notches
          responsive; otherwise the slower click-zoom curve applies.
        */}
        <div
          data-testid="lightbox-zoom-stage"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transition: dragging
              ? 'none'
              : wheelActive
                ? 'transform 0.12s ease-out'
                : 'transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform',
          }}
        >
          {/* Neu card frame around the image (design token with fallback). */}
          <div
            style={{
              background: 'var(--raised-bg, #edf1f8)',
              borderRadius: 20,
              padding: 7,
              boxShadow: '0 30px 80px -18px rgba(15,22,38,0.6)',
            }}
          >
            {/*
              onError is passed here as well so that opening the lightbox for
              an image whose token expired while the dialog was closed also
              triggers the retry. No click handler here — clicks are fully
              owned by the overlay state machine above.
            */}
            <img
              ref={baseRef}
              src={src}
              alt={alt}
              className="block object-contain"
              style={{ borderRadius: 14, maxHeight: '84vh', maxWidth: '86vw' }}
              onError={onError}
            />
          </div>
        </div>

        {/*
          Top bar: filename pill, zoom % pill (zoomed only), round close
          button. `stopMouse` keeps its presses out of the click machine.
          Right-click here keeps the NATIVE browser menu (propagation only is
          stopped): the custom Copy/Save menu belongs to the image surface.
        */}
        <div
          className="fixed left-[22px] right-[22px] top-[18px] flex items-center gap-2.5"
          style={{ cursor: 'default' }}
          onMouseDown={stopMouse}
          onMouseUp={stopMouse}
          onContextMenu={stopMouse}
        >
          <span
            style={{
              background: 'rgba(20,26,40,0.55)',
              color: 'rgba(255,255,255,0.92)',
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {alt}
          </span>
          {zoomed && (
            <span
              style={{
                background: 'rgba(20,26,40,0.55)',
                color: 'rgba(255,255,255,0.75)',
                borderRadius: 999,
                padding: '6px 12px',
                fontSize: 11.5,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Math.round(scale * 100)}%
            </span>
          )}
          <span className="flex-1" />
          <button
            type="button"
            aria-label="Close viewer"
            onClick={close}
            className="flex size-9 cursor-pointer items-center justify-center rounded-full border-0"
            style={{ background: 'rgba(20,26,40,0.55)', color: '#fff' }}
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Bottom hint pill — informational only, never a click target. */}
        <div className="pointer-events-none fixed bottom-[18px] left-0 right-0 flex justify-center">
          <span
            style={{
              background: 'rgba(20,26,40,0.5)',
              color: 'rgba(255,255,255,0.78)',
              borderRadius: 999,
              padding: '5px 14px',
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {zoomed
              ? 'Scroll to zoom · Drag to pan · Click to zoom out · Esc to close'
              : 'Click to close · Scroll or double-click to zoom · Right-click for options'}
          </span>
        </div>

        {/*
          Custom context menu. Deliberately NOT Radix ContextMenu: the whole
          overlay already owns every mouse event, and the approved design's
          self-contained fixed-position menu (with its swallow-dismiss press
          semantics) is simpler to keep faithful than fighting Radix's own
          dismiss layer inside a dialog. It stops mousedown/mouseup so a press
          on the menu is never mistaken for the dismiss press, and prevents +
          stops contextmenu so right-clicking the menu itself is inert.
        */}
        {menu !== null && (
          <div
            data-testid="lightbox-context-menu"
            className="fixed"
            style={{
              left: menu.x,
              top: menu.y,
              minWidth: 188,
              borderRadius: 16,
              padding: 6,
              background: 'var(--raised-bg, #edf1f8)',
              border: '1px solid var(--edge, rgba(96,110,132,0.14))',
              boxShadow:
                '0 18px 40px -12px rgba(45,55,75,0.45), 0 3px 10px rgba(45,55,75,0.18)',
              cursor: 'default',
            }}
            onMouseDown={stopMouse}
            onMouseUp={stopMouse}
            onContextMenu={(e: ReactMouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MenuItemButton
              icon={<Copy className="size-[15px]" />}
              label="Copy"
              onClick={() => {
                void doCopy();
              }}
            />
            <MenuItemButton
              icon={<Download className="size-[15px]" />}
              label="Save as…"
              onClick={() => {
                void doSave();
              }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
